/**
 * MinimumStockPage
 * 
 * Página para gerenciar estoque mínimo.
 * Permite upload de Excel com dados de vendas e análise com Gemini por marca.
 */

import React, { useState } from 'react';
import {
  Upload, Database, RotateCw, Settings2,
  Loader2, AlertCircle, CheckCircle
} from 'lucide-react';
import ErpFloatingMenu from '../../../components/ERP/ErpFloatingMenu/ErpFloatingMenu';
import BrandSearchSelect from '../../../components/ERP/Purchases/BrandSearchSelect/BrandSearchSelect';
import MinimumStockConfigModal from '../../../components/ERP/Purchases/MinimumStockConfigModal/MinimumStockConfigModal';
import { PURCHASES_QUICK_MENU_ITEMS } from '../../../config/purchasesQuickMenu';
import { minimumStockService } from '../../../services/purchasesApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import './MinimumStockPage.css';

const MinimumStockPage = () => {
  const { toast, showToast, hideToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [updating, setUpdating] = useState({});
  const [updatingAll, setUpdatingAll] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [calcConfig, setCalcConfig] = useState(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];
    
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showToast('Por favor, selecione um arquivo Excel (.xlsx ou .xls)', 'error');
      return;
    }

    setUploading(true);
    try {
      const response = await minimumStockService.upload(file);
      if (response.success) {
        showToast(`✅ ${response.data.imported} registros importados com sucesso!`, 'success');
        // Recarregar lista de marcas
        loadBrands();
      } else {
        showToast(response.message || 'Erro ao importar arquivo', 'error');
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      showToast(error.response?.data?.message || 'Erro ao processar arquivo', 'error');
    } finally {
      setUploading(false);
      // Limpar input
      event.target.value = '';
    }
  };

  const loadBrands = async () => {
    try {
      const response = await minimumStockService.getBrands();
      if (response.success) {
        setBrands(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  };

  React.useEffect(() => {
    loadBrands();
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await minimumStockService.getConfig();
      if (response.success) {
        setCalcConfig(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedBrands.length) {
      showToast('Por favor, selecione ao menos uma marca', 'warning');
      return;
    }

    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const response = selectedBrands.length === 1
        ? await minimumStockService.analyze(selectedBrands[0])
        : await minimumStockService.analyzeBatch(selectedBrands);

      if (response.success) {
        setAnalysisResult(response.data);
        showToast('Análise concluída com sucesso!', 'success');
      } else {
        showToast(response.message || 'Erro ao analisar', 'error');
      }
    } catch (error) {
      console.error('Erro ao analisar:', error);
      showToast(error.response?.data?.message || 'Erro ao analisar', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpdateProduct = async (productCode, suggestedMinimum) => {
    setUpdating(prev => ({ ...prev, [productCode]: true }));
    
    try {
      const response = await minimumStockService.updateProductMinStock(productCode, suggestedMinimum);
      if (response.success) {
        showToast(`Estoque mínimo do produto ${productCode} atualizado!`, 'success');
        // Atualizar o resultado para mostrar que foi atualizado
        setAnalysisResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            suggestions: prev.suggestions.map(s => 
              s.product_code === productCode 
                ? { ...s, updated: true }
                : s
            )
          };
        });
      } else {
        showToast(response.message || 'Erro ao atualizar', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      showToast(error.response?.data?.message || 'Erro ao atualizar estoque mínimo', 'error');
    } finally {
      setUpdating(prev => ({ ...prev, [productCode]: false }));
    }
  };

  const handleUpdateAll = async () => {
    if (!analysisResult || !analysisResult.suggestions || analysisResult.suggestions.length === 0) {
      showToast('Nenhuma sugestão disponível para atualizar', 'warning');
      return;
    }

    setUpdatingAll(true);

    const suggestions = analysisResult.suggestions;
    const chunkSize = 100;
    const aggregated = {
      updated: [],
      not_found: [],
      errors: []
    };

    try {
      for (let offset = 0; offset < suggestions.length; offset += chunkSize) {
        const chunk = suggestions.slice(offset, offset + chunkSize);
        const response = await minimumStockService.updateAllMinStock(null, chunk);

        aggregated.updated.push(...(response.data?.updated || []));
        aggregated.not_found.push(...(response.data?.not_found || []));
        aggregated.errors.push(...(response.data?.errors || []));
      }

      const updatedCount = aggregated.updated.length;
      const notFoundCount = aggregated.not_found.length;
      const errorCount = aggregated.errors.length;

      if (updatedCount === 0) {
        showToast(
          notFoundCount
            ? `Nenhum produto atualizado. ${notFoundCount} código(s) não encontrado(s).`
            : 'Nenhum produto atualizado.',
          'error'
        );
        return;
      }

      showToast(
        `${updatedCount} produtos atualizados` +
          (notFoundCount || errorCount
            ? ` (${notFoundCount} não encontrados, ${errorCount} com erro)`
            : ' com sucesso!'),
        'success'
      );

      const updatedCodes = new Set(
        aggregated.updated.map((item) => String(item.product_code))
      );
      setAnalysisResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          suggestions: prev.suggestions.map((s) => (
            updatedCodes.has(String(s.product_code))
              ? { ...s, updated: true }
              : s
          ))
        };
      });
    } catch (error) {
      console.error('Erro ao atualizar todos:', error);
      showToast(error.response?.data?.message || error.message || 'Erro ao atualizar estoque mínimo', 'error');
    } finally {
      setUpdatingAll(false);
    }
  };

  return (
    <div className="minimum-stock">
      <Toast toast={toast} onClose={hideToast} />

      <header className="minimum-stock-header">
        <div className="minimum-stock-header-main">
          <h1 className="minimum-stock-title">
            <Database size={28} />
            Estoque Mínimo
          </h1>
          <p className="minimum-stock-subtitle">
            Analise dados de vendas e otimize o estoque mínimo por marca
          </p>
        </div>
        <div className="minimum-stock-header-actions">
          <button
            type="button"
            className="minimum-stock-config-btn"
            onClick={() => setConfigModalOpen(true)}
            title="Configurar parâmetros de cálculo"
          >
            <Settings2 size={16} />
            Configurar
          </button>
          <input
            type="file"
            id="minimum-stock-file-upload"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            disabled={uploading}
            className="minimum-stock-file-input"
          />
          <label
            htmlFor="minimum-stock-file-upload"
            className="minimum-stock-upload-btn"
            title="Importar dados de vendas (Excel)"
          >
            {uploading ? (
              <Loader2 size={18} className="minimum-stock-spinner" />
            ) : (
              <Upload size={18} />
            )}
            <span>Importar Excel</span>
          </label>
        </div>
      </header>

      <ErpFloatingMenu items={PURCHASES_QUICK_MENU_ITEMS} />

      <section className="minimum-stock-card">
        <div className="minimum-stock-card-header">
          <Database size={22} />
          <h2>Análise de Estoque Mínimo</h2>
        </div>
        <p className="minimum-stock-card-description">
          Selecione uma ou mais marcas (ou todas) para analisar os dados de vendas e receber sugestões de estoque mínimo.
        </p>

        <div className="minimum-stock-controls">
          <BrandSearchSelect
            value={selectedBrands}
            brands={brands}
            onChange={setSelectedBrands}
            multiple
            label="Marcas"
            placeholder="Selecione uma ou mais marcas..."
            modalTitle="Selecionar Marcas"
            modalSubtitle="Escolha uma, várias ou todas as marcas para o cálculo"
            disabled={brands.length === 0 || analyzing}
          />

          <button
            type="button"
            className="minimum-stock-primary-btn"
            onClick={handleAnalyze}
            disabled={!selectedBrands.length || analyzing || brands.length === 0}
          >
            {analyzing ? (
              <>
                <Loader2 size={18} className="minimum-stock-spinner" />
                Analisando...
              </>
            ) : (
              <>
                <Database size={18} />
                Calcular Estoque
              </>
            )}
          </button>
        </div>

        {calcConfig && (
          <p className="minimum-stock-config-summary">
            Cálculo atual: {calcConfig.coverageMonths} {calcConfig.coverageMonths === 1 ? 'mês' : 'meses'} de cobertura
            {' · '}
            tendência em {calcConfig.trendMonths} {calcConfig.trendMonths === 1 ? 'mês' : 'meses'}
            {' · '}
            histórico de {calcConfig.historyMonths} meses
          </p>
        )}

        {brands.length === 0 && (
          <div className="minimum-stock-info-banner">
            <AlertCircle size={16} />
            <span>Nenhuma marca encontrada. Faça upload de dados primeiro.</span>
          </div>
        )}

        {analysisResult && (
          <div className="minimum-stock-results">
            <div className="minimum-stock-results-header">
              <h3>Resultados da Análise</h3>
              <button
                type="button"
                className="minimum-stock-secondary-btn"
                onClick={handleUpdateAll}
                disabled={updatingAll || analysisResult.suggestions.length === 0}
                title="Atualizar estoque mínimo de todos os produtos desta marca"
              >
                {updatingAll ? (
                  <>
                    <Loader2 size={16} className="minimum-stock-spinner" />
                    Atualizando...
                  </>
                ) : (
                  <>
                    <RotateCw size={16} />
                    Atualizar Todos
                  </>
                )}
              </button>
            </div>

            <div className="minimum-stock-summary">
              <div className="minimum-stock-summary-item">
                <span className="minimum-stock-summary-label">
                  {analysisResult.brands?.length > 1 ? 'Marcas' : 'Marca'}
                </span>
                <span className="minimum-stock-summary-value">{analysisResult.brand}</span>
              </div>
              <div className="minimum-stock-summary-item">
                <span className="minimum-stock-summary-label">Período</span>
                <span className="minimum-stock-summary-value">
                  {new Date(analysisResult.period_analyzed.start).toLocaleDateString('pt-BR')}
                  {' - '}
                  {new Date(analysisResult.period_analyzed.end).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className="minimum-stock-summary-item">
                <span className="minimum-stock-summary-label">Total de Produtos</span>
                <span className="minimum-stock-summary-value">{analysisResult.summary.total_products}</span>
              </div>
              {analysisResult.calculation_config && (
                <div className="minimum-stock-summary-item">
                  <span className="minimum-stock-summary-label">Cobertura</span>
                  <span className="minimum-stock-summary-value">
                    {analysisResult.calculation_config.coverage_months} meses
                  </span>
                </div>
              )}
            </div>

            <div className="minimum-stock-suggestions-grid">
              {analysisResult.suggestions.map((suggestion, index) => (
                <div key={`${suggestion.brand || 'brand'}-${suggestion.product_code}-${index}`} className={`minimum-stock-suggestion-card${suggestion.updated ? ' is-updated' : ''}`}>
                  <div className="minimum-stock-suggestion-header">
                    <div>
                      <span className="minimum-stock-product-code">{suggestion.product_code}</span>
                      {suggestion.product_name && (
                        <div className="minimum-stock-product-name">{suggestion.product_name}</div>
                      )}
                      {suggestion.brand && analysisResult.brands?.length > 1 && (
                        <div className="minimum-stock-product-brand">{suggestion.brand}</div>
                      )}
                    </div>
                    <div className="minimum-stock-suggestion-badges">
                      <span className={`minimum-stock-recommendation ${suggestion.recommendation}`}>
                        {suggestion.recommendation === 'increase' ? 'Aumentar'
                          : suggestion.recommendation === 'decrease' ? 'Diminuir' : 'Manter'}
                      </span>
                      {suggestion.updated && (
                        <CheckCircle size={16} className="minimum-stock-updated-icon" title="Estoque mínimo atualizado" />
                      )}
                    </div>
                  </div>
                  <div className="minimum-stock-suggestion-body">
                    <div className="minimum-stock-suggestion-row">
                      <span className="label">Atual:</span>
                      <span className="value">{suggestion.current_minimum}</span>
                    </div>
                    <div className="minimum-stock-suggestion-row">
                      <span className="label">Sugerido:</span>
                      <span className="value highlight">{suggestion.suggested_minimum}</span>
                    </div>
                    <div className="minimum-stock-suggestion-row">
                      <span className="label">Confiança:</span>
                      <span className={`confidence ${suggestion.confidence}`}>
                        {suggestion.confidence === 'high' ? 'Alta'
                          : suggestion.confidence === 'medium' ? 'Média' : 'Baixa'}
                      </span>
                    </div>
                    <div className="minimum-stock-suggestion-reason">
                      {suggestion.reason}
                    </div>
                    <div className="minimum-stock-suggestion-actions">
                      <button
                        type="button"
                        className="minimum-stock-update-btn"
                        onClick={() => handleUpdateProduct(suggestion.product_code, suggestion.suggested_minimum)}
                        disabled={updating[suggestion.product_code] || suggestion.updated}
                        title="Atualizar estoque mínimo deste produto"
                      >
                        {updating[suggestion.product_code] ? (
                          <Loader2 size={14} className="minimum-stock-spinner" />
                        ) : suggestion.updated ? (
                          <CheckCircle size={14} />
                        ) : (
                          <RotateCw size={14} />
                        )}
                        {updating[suggestion.product_code] ? 'Atualizando...' : suggestion.updated ? 'Atualizado' : 'Atualizar'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <MinimumStockConfigModal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        onSaved={(config) => {
          setCalcConfig(config);
          showToast('Configuração salva com sucesso', 'success');
        }}
      />
    </div>
  );
};

export default MinimumStockPage;
